// ═══════════════════════════════════════════════════════════════════════════
// « COMBIEN ÇA COÛTE POUR MA FIDUCIAIRE ? » — le calcul, et rien d'autre
// ───────────────────────────────────────────────────────────────────────────
// 📏 POURQUOI CE MODULE EXISTE, mesuré le 02/09/2026 sur la Search Console
// (export de 3 mois). Sept requêtes d'ACHAT venant de fiduciaires cumulent
// 1 869 affichages et ZÉRO clic :
//   prix de logiciel gestion fiduciaire ........... 416 · pos 12,0
//   combien coûte un logiciel de gestion fiduciaire  389 · pos 17,1
//   logiciel fiduciaire ........................... 312 · pos 14,4
//   logiciel de gestion fiduciaire belgique ....... 301 · pos 18,9
//   logiciel de gestion multi-dossiers fiduciaire . 168 · pos  8,8
//   outil de gestion pour fiduciaire en belgique .. 165 · pos 12,0
//   logiciel rgpd fiduciaire ...................... 118 · pos 17,9
//
// 🔑 CE QUI REND CE TERRAIN PRENABLE, et c'est mesuré aussi (docs/30-39
// Commercial/30.30, relevé du 24/08) : « AUCUN prestataire belge ne publie de
// prix à l'acte. Tous renvoient au devis personnalisé. » Deux seuls prix à
// l'acte trouvés sur tout le marché, chez UCM.
//
// Autrement dit : la question la plus posée sur ce marché est « combien ça
// coûte », et PERSONNE n'y répond. Nous, si — le tarif est public. Un
// concurrent qui voudrait faire la même page devrait publier des prix qu'il
// refuse de publier. C'est le seul avantage de ce dossier qui ne se rattrape
// pas avec un budget.
//
// ⚠️ CE MODULE NE CITE AUCUN PRIX DE CONCURRENT. Le relevé du 24/08 est formel :
// les fourchettes trouvées sont des ordres de grandeur NON ATTRIBUÉS. Publier
// « Securex coûte X » sans l'avoir relevé chez Securex serait inventer un
// chiffre sur un tiers — exactement ce que R2 interdit, en pire.
//
// ⚠️ ET IL NE REND JAMAIS ZÉRO POUR UNE DONNÉE ABSENTE. `Number(null)` vaut 0 :
// sans filtre, un champ vide afficherait « 0 € », c'est-à-dire une affirmation
// fausse. On rend `null`, et l'écran n'affiche rien. Même règle que
// `coutMensuel` dans conditions-commerciales.js.
// ═══════════════════════════════════════════════════════════════════════════
import { AUREUS_PRICING } from '@/app/lib/conditions-commerciales';

/** Un nombre utilisable, ou null. Refuse le vide, le négatif et le non-fini. */
function nombre(v) {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  if (!Number.isFinite(n) || n < 0) return null;
  return n;
}

/**
 * Le coût réel d'une fiduciaire, et ce qu'elle gagne en refacturant.
 *
 * @param {object} p
 * @param {number|string} p.fichesParMois   fiches de paie traitées, tous dossiers confondus
 * @param {number|string} [p.factureParFiche] ce que la fiduciaire refacture à son client
 * @param {number|string} [p.dossiers]      nombre de dossiers clients (facultatif)
 * @returns {object|null} null si le volume manque — jamais un zéro trompeur
 */
export function coutFiduciaire({ fichesParMois, factureParFiche, dossiers } = {}) {
  const fiches = nombre(fichesParMois);
  if (fiches === null) return null;

  const parFiche = AUREUS_PRICING.payrollSlipPrice;
  const coutMois = fiches * parFiche + AUREUS_PRICING.monthlyBaseFee;
  const setup = AUREUS_PRICING.setupFee;

  // La refacturation est FACULTATIVE : sans elle, on donne le coût et on se
  // tait sur la marge. Inventer un prix de vente à la place de la fiduciaire
  // serait lui faire dire un chiffre qu'elle n'a pas donné.
  const vente = nombre(factureParFiche);
  const marge = vente === null ? null : (vente - parFiche) * fiches;

  const nbDossiers = nombre(dossiers);
  // 0 dossier n'est pas une division valide : on rend null plutôt qu'Infinity.
  const parDossier = nbDossiers ? coutMois / nbDossiers : null;

  return {
    fiches,
    parFiche,
    coutMois,
    // La 1re année porte les frais de mise en route, une seule fois. Les
    // annoncer À PART plutôt que noyés dans un total : un frais découvert en
    // cours de route a déjà coûté un dossier (incident du 28/07/2026).
    setup,
    coutPremiereAnnee: coutMois * 12 + setup,
    coutAnneesSuivantes: coutMois * 12,
    margeMois: marge,
    margeAn: marge === null ? null : marge * 12,
    // ⚠️ Une marge NÉGATIVE se dit. La cacher ferait signer une fiduciaire qui
    // perd de l'argent à chaque fiche — elle s'en apercevrait au premier
    // trimestre, et ce serait pire.
    perte: marge !== null && marge < 0,
    dossiers: nbDossiers,
    parDossier,
  };
}

/**
 * Le prix de refacturation à partir duquel la fiduciaire gagne de l'argent.
 * C'est sa vraie question, et elle n'a rien à voir avec notre tarif :
 * au-dessus de notre prix par fiche, elle marge ; en dessous, elle paie.
 */
export function seuilRefacturation() {
  return AUREUS_PRICING.payrollSlipPrice;
}
